import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  ClipboardCheck,
  Package,
  Search,
  Upload,
  Users,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type HubStatus = "not_started" | "in_progress" | "errors" | "finished";

interface HubRow {
  id: number;
  jobId: number | null;
  name: string;
  source: string;
  status: HubStatus;
  touched: string | null;
  file: string | null;
  counts: { ee: number; plans: number; enr: number } | null;
  error?: string;
}

interface HubData {
  totals: {
    total: number;
    finished: number;
    inProgress: number;
    errors: number;
    notStarted: number;
  };
  clients: HubRow[];
}

const FILTER_OPTIONS: Array<{ id: "all" | HubStatus; label: string }> = [
  { id: "all", label: "All" },
  { id: "not_started", label: "Not started" },
  { id: "in_progress", label: "In progress" },
  { id: "finished", label: "Finished" },
  { id: "errors", label: "Errors" },
];

export default function MigrationHub() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  // URL-state for filter + search so brokers can deep-link / refresh.
  const [filter, setFilter] = useState<"all" | HubStatus>(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("status");
    return (FILTER_OPTIONS.find((o) => o.id === v)?.id ?? "all") as
      | "all"
      | HubStatus;
  });
  const [query, setQuery] = useState<string>(() => {
    return new URLSearchParams(window.location.search).get("q") ?? "";
  });

  useEffect(() => {
    const params = new URLSearchParams();
    if (filter !== "all") params.set("status", filter);
    if (query) params.set("q", query);
    const qs = params.toString();
    const next = qs ? `?${qs}` : "";
    if (window.location.search !== next) {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${next}`,
      );
    }
  }, [filter, query]);

  const hubQuery = useQuery<HubData>({
    queryKey: ["/api/migration/hub"],
  });

  const startJob = useMutation({
    mutationFn: async (clientId: number) => {
      const client = hubQuery.data?.clients.find((c) => c.id === clientId);
      const res = await apiRequest("POST", "/api/migration/jobs", {
        clientId,
        clientName: client?.name ?? "",
        sourcePlatform: "ease",
      });
      return (await res.json()) as { id: number };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/migration/hub"] });
      navigate(`/migration/${data.id}`);
    },
    onError: (err: any) =>
      toast({
        title: "Failed to start migration",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  const filtered = useMemo(() => {
    const rows = hubQuery.data?.clients ?? [];
    return rows.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false;
      if (query && !c.name.toLowerCase().includes(query.toLowerCase()))
        return false;
      return true;
    });
  }, [hubQuery.data, filter, query]);

  const totals = hubQuery.data?.totals ?? {
    total: 0,
    finished: 0,
    inProgress: 0,
    errors: 0,
    notStarted: 0,
  };

  const onRowAction = (row: HubRow) => {
    if (row.status === "not_started") {
      if (row.jobId != null) {
        navigate(`/migration/${row.jobId}`);
      } else {
        startJob.mutate(row.id);
      }
      return;
    }
    if (row.jobId != null) navigate(`/migration/${row.jobId}`);
  };

  return (
    <div className="container mx-auto max-w-6xl space-y-4 py-6">
      {/* Breadcrumb + heading */}
      <div>
        <Link
          href="/"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Dashboard
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          Client migrations
        </h1>
        <p className="text-sm text-muted-foreground">
          Bring each imported client's enrollment census into Administr.
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Finished"
          value={totals.finished}
          tone="text-emerald-600"
        />
        <StatCard
          label="In progress"
          value={totals.inProgress}
          tone="text-amber-600"
        />
        <StatCard
          label="Errors"
          value={totals.errors}
          tone="text-red-600"
        />
        <StatCard
          label="Not started"
          value={totals.notStarted}
          tone="text-muted-foreground"
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          {FILTER_OPTIONS.map((opt) => {
            const count =
              opt.id === "all"
                ? totals.total
                : opt.id === "not_started"
                  ? totals.notStarted
                  : opt.id === "in_progress"
                    ? totals.inProgress
                    : opt.id === "finished"
                      ? totals.finished
                      : totals.errors;
            const active = filter === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setFilter(opt.id)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "border-foreground bg-foreground text-background"
                    : "border-muted-foreground/20 bg-background hover:bg-muted/50"
                }`}
              >
                {opt.label}
                <span className="text-[11px] opacity-70">{count}</span>
              </button>
            );
          })}
          <div className="flex-1" />
          <div className="relative w-full sm:w-[220px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients…"
              className="h-8 pl-7 text-xs"
            />
          </div>
        </div>

        <CardContent className="p-0">
          {hubQuery.isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              {totals.total === 0
                ? "No clients yet. Bulk-import clients to get started."
                : "No clients match the active filters."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Census file</TableHead>
                    <TableHead>Counts</TableHead>
                    <TableHead>Last modified</TableHead>
                    <TableHead className="w-[120px] text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((row) => (
                    <HubTableRow
                      key={row.id}
                      row={row}
                      onAction={() => onRowAction(row)}
                      pending={startJob.isPending}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className={`mt-1 font-mono text-2xl font-semibold ${tone}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function HubTableRow({
  row,
  onAction,
  pending,
}: {
  row: HubRow;
  onAction: () => void;
  pending: boolean;
}) {
  const statusLabel =
    row.status === "finished"
      ? "Finished"
      : row.status === "in_progress"
        ? "In progress"
        : row.status === "errors"
          ? "Error"
          : "Not started";

  const statusBadge =
    row.status === "finished" ? (
      <Badge className="whitespace-nowrap border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        {statusLabel}
      </Badge>
    ) : row.status === "errors" ? (
      <Badge className="whitespace-nowrap border-red-300 bg-red-50 text-red-700 hover:bg-red-50">
        <AlertTriangle className="mr-1 h-3 w-3" />
        {statusLabel}
      </Badge>
    ) : row.status === "in_progress" ? (
      <Badge className="whitespace-nowrap border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-50">
        {statusLabel}
      </Badge>
    ) : (
      <Badge
        variant="outline"
        className="whitespace-nowrap text-muted-foreground"
      >
        {statusLabel}
      </Badge>
    );

  const actionLabel =
    row.status === "finished"
      ? "View"
      : row.status === "in_progress"
        ? "Resume"
        : row.status === "errors"
          ? "Fix"
          : "Start";
  const actionVariant: "default" | "outline" =
    row.status === "not_started" || row.status === "errors"
      ? "default"
      : "outline";

  const touched = row.touched
    ? formatDistanceToNow(new Date(row.touched), { addSuffix: true })
    : "—";

  return (
    <TableRow className={row.status === "errors" ? "bg-red-50/40" : undefined}>
      <TableCell className="py-3">
        <div className="font-medium">{row.name}</div>
        <div className="text-[11px] text-muted-foreground">
          from {row.source}
        </div>
      </TableCell>
      <TableCell>
        {statusBadge}
        {row.error && (
          <div className="mt-1 max-w-[260px] text-[11px] text-red-700">
            {row.error}
          </div>
        )}
      </TableCell>
      <TableCell className="font-mono text-[11.5px] text-muted-foreground">
        {row.file ?? (
          <span className="font-sans italic text-muted-foreground/70">
            not uploaded
          </span>
        )}
      </TableCell>
      <TableCell>
        {row.counts ? (
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-3 text-xs">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help items-center gap-1 text-blue-700 dark:text-blue-300">
                    <Users className="h-3 w-3" />
                    <span className="font-mono font-medium">{row.counts.ee}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {row.counts.ee} employee{row.counts.ee === 1 ? "" : "s"}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help items-center gap-1 text-purple-700 dark:text-purple-300">
                    <Package className="h-3 w-3" />
                    <span className="font-mono font-medium">{row.counts.plans}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {row.counts.plans} plan{row.counts.plans === 1 ? "" : "s"}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex cursor-help items-center gap-1 text-emerald-700 dark:text-emerald-300">
                    <ClipboardCheck className="h-3 w-3" />
                    <span className="font-mono font-medium">{row.counts.enr}</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {row.counts.enr} enrollment{row.counts.enr === 1 ? "" : "s"}
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {touched}
      </TableCell>
      <TableCell className="text-right">
        <Button
          size="sm"
          variant={actionVariant}
          onClick={onAction}
          disabled={pending && row.status === "not_started"}
        >
          {row.status === "not_started" && (
            <Upload className="mr-1 h-3 w-3" />
          )}
          {actionLabel}
          {row.status !== "not_started" && (
            <ArrowRight className="ml-1 h-3 w-3" />
          )}
        </Button>
      </TableCell>
    </TableRow>
  );
}
